import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Chip,
  Input,
} from "@nextui-org/react";
import { StandardCancelButton } from "../../components/StandardButtons";
import React, { useState, useEffect } from "react";
import {
  changeHandler,
  FileInput,
  TextAreaInput,
  TextInput,
} from "../FormBuilder/InputField/InputField";
import {
  EvidenceDescriptionSchema,
  EvidenceNameSchema,
  EvidenceCategorySchema,
  fileInputSchema,
  initialData,
} from "./evidence.constants";
import {
  PublishData,
  getDataWithFilterUrl,
  UpdateOneData,
} from "../../controllers/APIController";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

const AddEvidenceModal = ({
  id,
  isOpen,
  onOpen,
  onOpenChange,
  fullFilterUrl,
  pageContext = "Performance Data", // Default context, can be passed from parent
}) => {
  const [evidenceData, setEvidenceData] = useState(initialData);
  const [validationErrors, setValidationErrors] = useState({});
  const [metrics, setMetrics] = useState([]);
  const [responses, setResponses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [metricSearchTerm, setMetricSearchTerm] = useState("");
  const [responseSearchTerm, setResponseSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedMetricClass, setSelectedMetricClass] = useState("all");
  const clientQuery = useQueryClient();

  const UserLogged = JSON.parse(sessionStorage.getItem("userData"));
  const OrgInfo = UserLogged && UserLogged.user.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + OrgInfo;
  // Set category based on page context
  useEffect(() => {
    if (pageContext) {
      setEvidenceData((prev) => ({
        ...prev,
        Category: pageContext,
      }));
    }
  }, [pageContext]);

  // Fetch metrics for the organization\
  const { data: metricData } = useQuery({
    queryKey: ["metrics", orgFilterUrl],
    queryFn: () => getDataWithFilterUrl("metrics", orgFilterUrl),
  });
  useEffect(() => {
    const metricsList = metricData?.data || [];
    setMetrics(metricsList);
  }, [metricData]);

  // Fetch responses when a metric is selected or date range changes
  useEffect(() => {
    const fetchResponses = async () => {
      if (!evidenceData["Related Metric"]) {
        console.log("No metric selected");
        setResponses([]);
        return;
      }

      try {
        console.log("Selected metric ID:", evidenceData["Related Metric"]);
        console.log("Available metrics:", metrics);

        // Get the selected metric to extract its name
        const selectedMetric = metrics.find(
          (metric) => metric.id === parseInt(evidenceData["Related Metric"])
        );
        console.log("Selected metric object:", selectedMetric);

        if (!selectedMetric) {
          console.log("Selected metric not found in metrics list");
          setResponses([]);
          return;
        }

        const metricName =
          selectedMetric.attributes?.Name || selectedMetric.attributes?.name;
        console.log("Extracted metric name:", metricName);

        if (!metricName) {
          console.log("No metric name found in selected metric");
          setResponses([]);
          return;
        }

        // Try both field name variations since the console shows Metric_Id (capitalized)
        let metricFilterUrl = `filters[Metric_Id][$eqi]=${encodeURIComponent(
          metricName
        )}`;
        console.log("Base filter URL:", metricFilterUrl);
        console.log("Metric name being filtered for:", metricName);

        // Add date range filter if specified
        if (startDate && endDate) {
          const startDateObj = new Date(startDate);
          const endDateObj = new Date(endDate);
          endDateObj.setDate(endDateObj.getDate() + 1); // Next day to include all responses from the end date

          const startDateStr = startDateObj.toISOString();
          const endDateStr = endDateObj.toISOString();

          metricFilterUrl += `&filters[createdAt][$gte]=${startDateStr}&filters[createdAt][$lt]=${endDateStr}`;
          console.log("With date range filter:", metricFilterUrl);
        } else if (startDate) {
          // If only start date is provided, use it as a single day filter
          const startDateObj = new Date(startDate);
          const endDateObj = new Date(startDate);
          endDateObj.setDate(endDateObj.getDate() + 1);

          const startDateStr = startDateObj.toISOString();
          const endDateStr = endDateObj.toISOString();

          metricFilterUrl += `&filters[createdAt][$gte]=${startDateStr}&filters[createdAt][$lt]=${endDateStr}`;
          console.log("With single date filter:", metricFilterUrl);
        } else {
          console.log("No date filter applied");
        }

        console.log("Final filter URL:", metricFilterUrl);
        const data = await getDataWithFilterUrl("responses", metricFilterUrl);
        console.log("Raw API response:", data);

        const responsesList = data?.data || [];
        console.log("Processed responses list:", responsesList);
        console.log("Total responses returned:", responsesList.length);

        // Check the first few responses to see if filtering is working
        if (responsesList.length > 0) {
          console.log(
            "First response Metric_Id:",
            responsesList[0]?.attributes?.Metric_Id
          );
          console.log(
            "Second response Metric_Id:",
            responsesList[1]?.attributes?.Metric_Id
          );
          console.log(
            "Third response Metric_Id:",
            responsesList[2]?.attributes?.Metric_Id
          );
        }

        setResponses(responsesList);
      } catch (error) {
        console.error("Error fetching responses:", error);
        setResponses([]);
      }
    };

    fetchResponses();
  }, [evidenceData["Related Metric"], startDate, endDate, metrics]);

  // Get unique metric class names
  const metricClasses = [
    ...new Set(
      metrics.map(
        (metric) =>
          metric.attributes?.ClassName ||
          metric.attributes?.class_name ||
          "Uncategorized"
      )
    ),
  ].sort();

  // Filter metrics based on search term and class name
  const filteredMetrics = metrics.filter((metric) => {
    // Class name filter
    if (selectedMetricClass !== "all") {
      const metricClass =
        metric.attributes?.ClassName ||
        metric.attributes?.class_name ||
        "Uncategorized";
      if (metricClass !== selectedMetricClass) {
        return false;
      }
    }

    // Search term filter
    if (!metricSearchTerm) return true;
    const metricName = metric.attributes?.Name || metric.attributes?.name || "";
    return metricName.toLowerCase().includes(metricSearchTerm.toLowerCase());
  });

  // Filter responses based on search term only (date filtering is done at backend level)
  const filteredResponses = responses.filter((response) => {
    if (!responseSearchTerm) return true;
    const responseName =
      response.attributes?.JsonSchema?.flatData?.Name ||
      response.attributes?.JsonSchema?.flatData?.name ||
      response.attributes?.JsonSchema?.name ||
      response.attributes?.JsonSchema?.Name ||
      "";
    return responseName
      .toLowerCase()
      .includes(responseSearchTerm.toLowerCase());
  });

  const validationHandler = () => {
    setValidationErrors({});
    let isValid = true;
    const err = {};

    if (evidenceData["Name"].trim() === "") {
      err["Name"] = "Name is required!";
      isValid = false;
    }
    if (
      evidenceData["Upload Evidence"] === "" ||
      evidenceData["Upload Evidence"] === null ||
      evidenceData["Upload Evidence"] === undefined
    ) {
      err["Upload Evidence"] = "File is required!";
      isValid = false;
    }

    setValidationErrors(err);
    return isValid;
  };

  const submitHandler = async (onClose) => {
    const validationResponse = validationHandler();
    if (!validationResponse) return;

    setIsLoading(true);

    try {
      console.log("Evidence Data:", evidenceData["Related Metric"]);
      // Create the evidence entry
      const publishData = {
        Name: evidenceData["Name"],
        Description: evidenceData["Description"],
        Category: evidenceData["Category"],
        EvidenceUrl: evidenceData["Upload Evidence"]?.url,
        EvidenceId: evidenceData["Upload Evidence"]?.id,
        Organization: OrgInfo,
        ReferenceList: {
          connect: [
            {
              id: evidenceData["Related Metric"],
              position: { end: true },
            },
          ],
          disconnect: [],
        },
      };

    
      const evidenceResponse = await PublishData(
        { data: publishData },
        "evidences"
      );
      const evidenceId = evidenceResponse?.data?.id;

      if (evidenceId && evidenceData["Related Response Data"].length > 0) {
        // Update each selected response to include this evidence in its ReferenceList
        for (const responseId of evidenceData["Related Response Data"]) {
          try {
            // Get current response data
            const responseData = await getDataWithFilterUrl(
              "responses",
              `filters[id][$eqi]=${responseId}`
            );
            const currentResponse = responseData?.data?.[0];

            if (currentResponse) {
              // Get existing ReferenceList or create new one
              const existingReferenceList =
                currentResponse.attributes.ReferenceList?.data || [];
              const updatedReferenceList = [
                ...existingReferenceList,
                { id: evidenceId },
              ];

              // Update the response with new evidence reference
              await UpdateOneData("responses", responseId, {
                ReferenceList: updatedReferenceList,
              });
            }
          } catch (error) {
            console.error(`Error updating response ${responseId}:`, error);
          }
        }
      }
      clientQuery.invalidateQueries({ queryKey: ["evidences", fullFilterUrl] });
      onClose();
    } catch (error) {
      console.error("Error creating evidence:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      setValidationErrors({});
      setEvidenceData({
        ...initialData,
        Category: pageContext, // Preserve the category
      });
      setResponses([]);
      setMetricSearchTerm("");
      setResponseSearchTerm("");
      setStartDate("");
      setEndDate("");
      setSelectedMetricClass("all");
    }
    onOpenChange(isOpen);
  };

  const handleMetricChange = (value) => {
    setEvidenceData((prev) => ({
      ...prev,
      "Related Metric": value,
      "Related Response Data": [], // Reset response selection when metric changes
    }));
    setResponseSearchTerm(""); // Reset response search when metric changes
    setStartDate(""); // Reset date range when metric changes
    setEndDate("");
  };

  const handleMetricClassChange = (value) => {
    setSelectedMetricClass(value);
    // Reset metric selection when class changes
    setEvidenceData((prev) => ({
      ...prev,
      "Related Metric": "",
      "Related Response Data": [],
    }));
    setMetricSearchTerm("");
    setResponseSearchTerm("");
    setStartDate("");
    setEndDate("");
  };

  const handleResponseSelection = (selectedKeys) => {
    setEvidenceData((prev) => ({
      ...prev,
      "Related Response Data": Array.from(selectedKeys),
    }));
  };

  const getResponseDisplayName = (response) => {
    // Check for name in the new flatData structure first
    const name =
      response.attributes?.JsonSchema?.flatData?.Name ||
      response.attributes?.JsonSchema?.flatData?.name ||
      response.attributes?.JsonSchema?.name ||
      response.attributes?.JsonSchema?.Name ||
      `Response ${response.id}`;
    const date = new Date(
      response.attributes?.createdAt || response.attributes?.updatedAt
    ).toLocaleDateString();
    return `${name} (${date})`;
  };

  const getDateRangeText = () => {
    if (startDate && endDate) {
      return `${new Date(startDate).toLocaleDateString()} - ${new Date(
        endDate
      ).toLocaleDateString()}`;
    } else if (startDate) {
      return new Date(startDate).toLocaleDateString();
    }
    return "Select date range";
  };

  return (
    <>
      <Modal isOpen={isOpen} onOpenChange={handleOpenChange} size="2xl">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                Add Evidence
              </ModalHeader>
              <ModalBody>
                <TextInput
                  data={EvidenceNameSchema}
                  value={evidenceData["Name"]}
                  validationErrors={validationErrors}
                  onChangeHandler={(title, value, type) =>
                    changeHandler(
                      title,
                      value,
                      type,
                      evidenceData,
                      setEvidenceData
                    )
                  }
                  cssStyle="my-4"
                />

                <TextInput
                  data={EvidenceCategorySchema}
                  value={evidenceData["Category"]}
                  validationErrors={validationErrors}
                  onChangeHandler={(title, value, type) =>
                    changeHandler(
                      title,
                      value,
                      type,
                      evidenceData,
                      setEvidenceData
                    )
                  }
                  cssStyle="mb-4"
                />

                <TextAreaInput
                  data={EvidenceDescriptionSchema}
                  value={evidenceData["Description"]}
                  validationErrors={validationErrors}
                  onChangeHandler={(title, value, type) =>
                    changeHandler(
                      title,
                      value,
                      type,
                      evidenceData,
                      setEvidenceData
                    )
                  }
                  cssStyle="mb-4"
                />

                <div className="mb-4">
                  <label className="text-sm font-medium text-neutral-700 mb-2 block">
                    Related Metric
                  </label>
                  <div className="space-y-2">
                    <Select
                      placeholder="Filter by metric class"
                      value={selectedMetricClass}
                      onChange={(e) => handleMetricClassChange(e.target.value)}
                      size="sm"
                      aria-label="Filter by metric class"
                    >
                      <SelectItem key="all" value="all" textValue="All Classes">
                        All Classes
                      </SelectItem>
                      {metricClasses.map((className) => (
                        <SelectItem
                          key={className}
                          value={className}
                          textValue={className}
                        >
                          {className}
                        </SelectItem>
                      ))}
                    </Select>
                    <Select
                      placeholder="Search and select a metric"
                      value={evidenceData["Related Metric"]}
                      onChange={(e) => handleMetricChange(e.target.value)}
                      size="sm"
                      onInputChange={setMetricSearchTerm}
                      inputValue={metricSearchTerm}
                      items={filteredMetrics}
                      isDisabled={filteredMetrics.length === 0}
                      aria-label="Search and select a metric"
                    >
                      {(metric) => (
                        <SelectItem
                          key={metric.id}
                          value={metric.id}
                          textValue={
                            metric.attributes?.Name ||
                            metric.attributes?.name ||
                            `Metric ${metric.id}`
                          }
                        >
                          {metric.attributes?.Name ||
                            metric.attributes?.name ||
                            `Metric ${metric.id}`}
                        </SelectItem>
                      )}
                    </Select>
                    {filteredMetrics.length === 0 &&
                      selectedMetricClass !== "all" && (
                        <p className="text-xs text-neutral-500">
                          No metrics found in the selected class. Try selecting
                          a different class or search term.
                        </p>
                      )}
                  </div>
                </div>

                {evidenceData["Related Metric"] && (
                  <div className="mb-4">
                    <label className="text-sm font-medium text-neutral-700 mb-2 block">
                      Related Response Data
                    </label>
                    <p className="text-xs text-neutral-500 mb-2">
                      Select responses for the chosen metric. Use date range to
                      find responses from specific dates.
                    </p>
                    <div className="mb-2 space-y-2">
                      <div className="flex gap-2">
                        <Input
                          type="date"
                          placeholder="Start date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          startContent={
                            <FontAwesomeIcon
                              icon={faCalendar}
                              className="text-neutral-400 text-sm"
                            />
                          }
                          size="sm"
                          className="flex-1"
                        />
                        <Input
                          type="date"
                          placeholder="End date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          startContent={
                            <FontAwesomeIcon
                              icon={faCalendar}
                              className="text-neutral-400 text-sm"
                            />
                          }
                          size="sm"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    {responses.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-sm text-neutral-500">
                          {startDate
                            ? `No responses found for the selected metric in date range: ${getDateRangeText()}`
                            : "No responses found for the selected metric. Try selecting a date range."}
                        </p>
                        <Button
                          size="sm"
                          variant="flat"
                          color="primary"
                          className="mt-2"
                          onClick={async () => {
                            if (evidenceData["Related Metric"]) {
                              const selectedMetric = metrics.find(
                                (metric) =>
                                  metric.id ===
                                  parseInt(evidenceData["Related Metric"])
                              );
                              const metricName =
                                selectedMetric?.attributes?.Name ||
                                selectedMetric?.attributes?.name;
                              if (metricName) {
                                console.log(
                                  "Testing API call without date filter for metric:",
                                  metricName
                                );
                                const testUrl = `filters[Metric_Id][$eqi]=${encodeURIComponent(
                                  metricName
                                )}`;
                                console.log("Test URL:", testUrl);
                                try {
                                  const testData = await getDataWithFilterUrl(
                                    "responses",
                                    testUrl
                                  );
                                  console.log("Test API response:", testData);
                                } catch (error) {
                                  console.error("Test API error:", error);
                                }
                              }
                            }
                          }}
                        >
                          Test API Call (No Date Filter)
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Select
                          placeholder="Search and select response data"
                          selectionMode="multiple"
                          selectedKeys={
                            new Set(evidenceData["Related Response Data"])
                          }
                          onSelectionChange={handleResponseSelection}
                          size="sm"
                          onInputChange={setResponseSearchTerm}
                          inputValue={responseSearchTerm}
                          items={filteredResponses}
                          aria-label="Search and select response data"
                        >
                          {(response) => (
                            <SelectItem
                              key={response.id}
                              value={response.id}
                              textValue={getResponseDisplayName(response)}
                            >
                              {getResponseDisplayName(response)}
                            </SelectItem>
                          )}
                        </Select>
                        {evidenceData["Related Response Data"].length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {evidenceData["Related Response Data"].map(
                              (responseId) => {
                                const response = responses.find(
                                  (r) => r.id === responseId
                                );
                                return (
                                  <Chip
                                    key={responseId}
                                    size="sm"
                                    variant="flat"
                                    color="primary"
                                  >
                                    {response
                                      ? getResponseDisplayName(response)
                                      : `Response ${responseId}`}
                                  </Chip>
                                );
                              }
                            )}
                          </div>
                        )}
                        {filteredResponses.length === 0 &&
                          responseSearchTerm && (
                            <p className="text-xs text-neutral-500 mt-1">
                              No responses found matching your search term
                            </p>
                          )}
                      </>
                    )}
                  </div>
                )}

                <FileInput
                  data={fileInputSchema}
                  value={evidenceData["Upload Evidence"]}
                  inputfiledId="upload-evidence"
                  onChangeHandler={(title, value, type) => {
                    changeHandler(
                      title,
                      value,
                      type,
                      evidenceData,
                      setEvidenceData
                    );
                  }}
                  validationErrors={validationErrors}
                  cssStyle="mb-8"
                />
              </ModalBody>
              <ModalFooter>
                <StandardCancelButton onPress={onClose}>
                  Cancel
                </StandardCancelButton>
                <Button
                  color="primary"
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 font-medium"
                  onClick={() => submitHandler(onClose)}
                  isLoading={isLoading}
                >
                  Upload
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default AddEvidenceModal;
